/**
 * Challenge use cases – video challenges (submit, vote) and legacy (join by weight).
 */
import { challengeRepo } from '../../data/repositories/ChallengeRepository';
import { challengeEntryRepo } from '../../data/repositories/ChallengeEntryRepository';
import { challengeSubmissionRepo } from '../../data/repositories/ChallengeSubmissionRepository';
import { challengeVoteRepo } from '../../data/repositories/ChallengeVoteRepository';
import { uploadChallengeVideo } from '../../data/firebase/storage';

// ----- Video challenges (active challenges with status/endDate) -----

export function subscribeActiveChallenges(limitCount, onUpdate) {
  return challengeRepo.subscribeActive(limitCount, onUpdate);
}

export async function getChallenge(challengeId) {
  return challengeRepo.getById(challengeId);
}

/** One submission per user per challenge. Returns submission id or throws if already submitted. */
export async function submitChallengeVideo(challengeId, userId, videoFile) {
  const existing = await challengeSubmissionRepo.getByChallengeAndUser(challengeId, userId);
  if (existing) throw new Error('You can submit only one video per challenge.');
  const videoUrl = await uploadChallengeVideo(challengeId, userId, videoFile);
  return challengeSubmissionRepo.create({ challengeId, userId, videoUrl });
}

/** Create submission with pre-uploaded videoUrl (e.g. after compress + upload with progress). */
export async function createChallengeSubmission(challengeId, userId, videoUrl) {
  const existing = await challengeSubmissionRepo.getByChallengeAndUser(challengeId, userId);
  if (existing) throw new Error('You can submit only one video per challenge.');
  return challengeSubmissionRepo.create({ challengeId, userId, videoUrl });
}

export async function getMyChallengeSubmission(challengeId, userId) {
  return challengeSubmissionRepo.getByChallengeAndUser(challengeId, userId);
}

export function subscribeChallengeSubmissions(challengeId, limitCount, onUpdate) {
  return challengeSubmissionRepo.subscribeByChallenge(challengeId, limitCount, onUpdate);
}

/** One vote per user per submission. Returns { voted: true } or throws if already voted. */
export async function voteChallengeSubmission(submissionId, userId) {
  const already = await challengeVoteRepo.hasUserVoted(submissionId, userId);
  if (already) throw new Error('You can vote only once per video.');
  const { added } = await challengeVoteRepo.add(submissionId, userId);
  if (added) await challengeSubmissionRepo.incrementVoteCount(submissionId);
  return { voted: true };
}

export async function getVotedSubmissionIds(userId, submissionIds) {
  if (!userId || submissionIds.length === 0) return new Set();
  return challengeVoteRepo.getVotedSubmissionIds(userId, submissionIds);
}

// ----- Legacy (weight-based challenges) -----

export async function createChallenge(creatorId, { exercise, rules, durationDays }) {
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + (durationDays || 14));
  return challengeRepo.create({
    creatorId,
    exercise,
    rules: rules || {},
    participants: [],
    expiresAt,
  });
}

export function subscribeChallenges(limitCount, onUpdate) {
  return challengeRepo.subscribeList(limitCount, onUpdate);
}

export async function joinChallenge(challengeId, userId, workoutId, score) {
  const existing = await challengeEntryRepo.getByChallengeAndUser(challengeId, userId);
  if (existing && existing.score >= score) return existing.id;
  return challengeEntryRepo.create({ challengeId, userId, workoutId, score });
}

export function subscribeChallengeLeaderboard(challengeId, limitCount, onUpdate) {
  return challengeEntryRepo.subscribeByChallenge(challengeId, limitCount, onUpdate);
}

export async function getChallengeLeaderboard(challengeId, limitCount = 100) {
  return challengeEntryRepo.listByChallenge(challengeId, limitCount);
}
